import { IOrderUser } from '~api/CartAPI/ICartApiSdk';
import { serviceNames } from '~setup/CartService';
import { diContainer } from '~setup/diContainer';
import { toBoolean } from '~utils/ToBoolean';

import { ICartOrderData, ICartTotals } from '~domain/entities/Cart/ICart';
import ICartItem from '~domain/entities/CartItem/ICartItem';

import { IRawVulcanResponse } from './IVulcanResponse';

export const VULCAN_CART_SCHEMA = {
    cartTotals: {
        totalCost: 'trans_total_cost',
        subTotal: 'subtotal_no_tax',
        taxRate: 'tax_rate',
        isVatApplied: 'is_vat_applied',
        totalTax: 'total_tax',
        shippingTax: 'trans_total_shipping_applied_price',
        'discount.isDiscounted': 'trans_is_discounted',
        'discount.totalPrice': 'trans_total_items_base_price',
        'discount.discountAmount': 'totalItemsDiscount',
        'discount.offersSummary': 'offers_summary',
        maxTotalItemLimit: 'max_total_item_limit',
        hasSalesTax: 'display_sales_tax_cart',
        giftwrapAppliedPrice: 'trans_total_giftwrap_applied_price',
        donationPrice: 'donation_price',
        paymentInstallments: 'payment_installments',
        retailDeliveryFee: 'trans_total_retail_delivery_fee'
    },
    cartOrderData: {
        messages: 'messages',
        offers: 'offers',
        wp_samples: 'wp_samples',
        pwp: 'pwp',
        is_giftWrap: 'order_gift_wrap',
        SHIP_METHOD: 'order_ship_method',
        ADDRESS_ID: 'order_address_id'
    },
    cartItems: [
        { name: 'items' },
        {
            cartId: 'cart_id',
            collectionId: 'collection_id',
            discountType: 'discount_type',
            offerCode: 'offer_code',
            quantity: 'quantity',
            skuId: 'sku_id',
            basePrice: 'base_price',
            appliedPrice: 'total_price',
            appliedPriceWithTax: 'total_price_with_tax',
            discountPercent: 'discount_percent',
            discountedUnitPrice: 'discounted_unit_price',
            discountedUnitPriceWithTax: 'discounted_unit_price_with_tax',
            hasMarkdownDiscount: 'has_mark_down_price',
            unitPriceWithTax: 'unit_price_with_tax',
            isOutOfStock: 'is_oos',
            isReplenishable: 'is_replenishable',
            isGiftItem: 'is_gift_item',
            isWpOffer: 'is_wp_offer',
            isEngravable: 'is_engravable',
            'product.productId': 'product_id',
            'product.name': 'name',
            'product.description': 'short_desc',
            'product.skuLabel': 'name',
            'product.url': 'url',
            'product.price': 'price',
            'product.shade': 'shade_name',
            'product.shadeCode': 'shade_color',
            'product.size': 'size',
            'product.thumb': 'thumbnail',
            'product.altThumb': 'thumbnails',
            'product.baseName': 'name',
            'product.maxAllowedQty': 'max_allowed_qty',
            replenishmentOptions: 'replenishment_opt_group',
            'replenishmentInfo.collectionId': 'collection_id',
            'replenishmentInfo.replenishmentToken': 'replenishment_csrf_token'
        }
    ]
};

export const parseTotals = ({ data }: IRawVulcanResponse): ICartTotals => {
    if (!data) {
        return {} as ICartTotals;
    }

    return {
        totalCost: data.trans_total_cost,
        formattedTotalCost: '', // pending
        subTotal: data.subtotal_no_tax,
        formattedSubtotal: '', // pending
        taxRate: data.tax_rate,
        isVatApplied: data.is_vat_applied,
        totalTax: data.total_tax,
        formattedTotalTax: '', // pending
        shippingTax: data.trans_total_shipping_applied_price,
        formattedShippingTax: '', // pending
        discount: {
            isDiscounted: toBoolean(data.trans_is_discounted),
            totalPrice: data.trans_total_items_base_price,
            formattedTotalPrice: '',
            discountAmount: data.totalItemsDiscount ?? 0,
            formattedDiscountAmount: '', // pending
            subtotalWithoutDonation: 0, // pending
            offersSummary: data.offers_summary
        },
        maxTotalItemLimit: data.max_total_item_limit,
        hasSalesTax: data.display_sales_tax_cart,
        giftwrapAppliedPrice: data.trans_total_giftwrap_applied_price,
        formattedGiftwrapAppliedPrice: '', // pending
        donationPrice: data.donation_price,
        paymentInstallments: data.payment_installments,
        retailDeliveryFee: data.trans_total_retail_delivery_fee
    };
};

export const reduceToCartItems = ({ data }: IRawVulcanResponse) => {
    if (!data) {
        return [];
    }

    const { config } = diContainer.get(serviceNames.config);

    const { items, applied_offers_data } = data;
    const enableReplenishment = config?.cartConfig?.enableReplenishment;

    return items.reduce((itemsAccum, item) => {
        const offerCode = item?.offer_code || '';

        const isItemFromAutomaticOffer =
            offerCode && applied_offers_data?.[0]?.[offerCode]?.activation === 'automatic';

        const cartItem: ICartItem = {
            cartId: item?.cart_id,
            collectionId: item?.collection_id,
            discountType: item.discount_type,
            offerCode: item.offer_code,
            quantity: item.quantity,
            skuId: item.sku_id.toString(),
            // Prices
            basePrice: item.base_price,
            appliedPrice: item.total_price,
            appliedPriceWithTax: item.total_price_w_tax,
            discountPercent: item?.discount_percent,
            discountedUnitPrice: item?.discounted_unit_price,
            discountedUnitPriceWithTax: item.discounted_unit_price_w_tax,
            hasMarkdownDiscount: item.has_mark_down_price,
            unitPriceWithTax: item.unit_price_w_tax,
            // Flags
            isOutOfStock: item.is_oos,
            isReplenishable: item.is_replenishable,
            isGiftItem: item.is_gift_item,
            isDonationItem: item.item_type === 'cart_donation',
            isFreeVirtualSkuItem: item.isFreeVirtualSkuItem || false,
            isWpOffer: item.is_wp_offer,
            isEngravable: item.is_engravable,
            isAutomaticOffer: !!isItemFromAutomaticOffer,
            // Product
            product: {
                productId: item.product_id,
                name: item.name,
                description: item.short_desc,
                skuLabel: item.name,
                url: item.url,
                price: item.price,
                shade: item.shade_name,
                shadeCode: item.shade_color,
                size: item.size,
                thumb: item.thumbnail,
                altThumb: item.thumbnails,
                isSample: item.item_type === 'cart_sample',
                isPricedSample: item.is_priced_sample,
                baseName: item.name,
                maxAllowedQty: item.max_allowed_qty
            },
            // Replenishment
            enableReplenishment: enableReplenishment,
            replenishmentOptions: item?.replenishment_opt_group ?? [],
            replenishmentInfo: {
                collectionId: item.collection_id ?? 0,
                replenishmentToken: item?.replenishment_csrf_token ?? ''
            }
        };

        return [...itemsAccum, cartItem];
    }, []);
};

export const reduceToCartOrderData = ({ data }: IRawVulcanResponse): ICartOrderData => {
    if (!data) {
        return {} as ICartOrderData;
    }

    const {
        messages,
        offers = {},
        user = {} as IOrderUser,
        wp_samples = [],
        pwp = [],
        order_gift_wrap,
        order_ship_method,
        order_address
    } = data;

    const is_giftWrap = toBoolean(order_gift_wrap || 0);
    const SHIP_METHOD = order_ship_method ?? 0;
    const ADDRESS_ID = order_address?.ADDRESS_ID ?? 0;

    return { messages, offers, user, is_giftWrap, SHIP_METHOD, wp_samples, pwp, ADDRESS_ID };
};
